import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { images } from '../utils/images';
import { 
  CheckCircle2, 
  Users, 
  Heart,
  Phone,
  Shield,
  Award,
  Clock,
  Target,
  TrendingUp,
  BookOpen
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StickyCallButton from '../components/StickyCallButton';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import VideoBackground from '../components/VideoBackground';

const MentalHealthPage = () => {
  const expectations = [
    "We assist you to gather supporting evidence and complete the access request form.",
    "Collaborate with health services and general practitioners/private psychiatrists.",
    "Act as a contact person for NDIS queries, at your request.",
    "Collect additional evidence, if required by the NDIS, to ensure timely completion of the access process.",
    "Follow-up with the NDIS on the status of the application.",
    "We assist you to understand and seek a review of the NDIS decision or appeal the decision only if required."
  ];

  const benefits = [
    {
      icon: Award,
      text: "You will receive high quality services"
    },
    {
      icon: Shield,
      text: "The provider meets with government requirements"
    },
    {
      icon: CheckCircle2,
      text: "You will have peace of mind knowing that your provider is reputable and has a good track record"
    },
    {
      icon: Shield,
      text: "The provider has the necessary insurance in place"
    },
    {
      icon: Users,
      text: "You will be able to access support if you have any problems or concerns"
    }
  ];

  const whyChooseUs = [
    "Friendly and well trained staff: Our team is skilled and trained to support people with disabilities with care and understanding.",
    "Flexible Support: We adjust our services to suit your needs as they change over time.",
    "Working Together: We work closely with your family, carers, and other professionals to give you the best support.",
    "Always Improving: We regularly check how we're doing and look for ways to make our services better.",
    "We Listen to You: Your precious feedback matters to us. We welcome suggestions to improve how we support you.",
    "Helpful Tools and Resources: We give you proper guidance, and support you need to reach your goals and dreams.",
    "Support for All Areas of Life: We look after your full well-being not just one part including health, home, work, and social life.",
    "Respect for You: We treat you with kindness, respect, and value your individuality.",
    "Safe and Legal Services: We follow all rules and standards to keep you safe and well supported.",
    "Clear Communication: We are open, clear and honest about what we offer and what it costs.",
    "Quick and Reliable Help: We aim to provide services on time and without unnecessary delays.",
    "Easy Feedback and Complaints: You and your family can easily share with us if something's not right and we'll work to fix it super quick."
  ];

  const faqs = [
    {
      question: "What does a mental health NDIS provider do?",
      answer: "At Life Care and Wellness, we support individuals living with psychosocial disability to access the services they need through the NDIS. The NDIS provides funding to eligible people with psychosocial disability and covers supports that focus on improving everyday functioning and independence."
    },
    {
      question: "How do I access mental health services through the NDIS?",
      answer: "To access mental health services through the NDIS, you will first need to decide if you are eligible for the NDIS. If eligible, you can apply through your local area coordinator or by contacting Life Care and Wellness directly. You'll need to provide proof of your psychosocial disability and how it impacts your daily life. If you already receive mental health services, you can discuss with your current providers."
    },
    {
      question: "Can I get NDIS funding for mental health support?",
      answer: "At Life Care and Wellness, we guide you to access the right support through us. While we fund functional supports that help you build skills, gain independence, and improve your ability to take part in everyday activities. Visit lifecarewellness.au to learn how we can support you on your journey."
    },
    {
      question: "How do I choose the right NDIS mental health provider in Brisbane or Logan?",
      answer: "At Life Care and Wellness, we proudly serve participants across Brisbane and Logan, offering flexible, recovery focused NDIS mental health support that puts you at the centre. Choosing the right NDIS mental health provider is an important step in your recovery journey."
    },
    {
      question: "Do you provide in-home or community mental health support?",
      answer: "We provide in-home and community based mental health support to suit your needs. Our team is here to help you: Build daily living skills, Stay socially connected, Manage personal goals, Access appointments, activities and local services. Ready to get started? Visit lifecarewellness.au or book an appointment"
    },
    {
      question: "What qualifications do your support workers have?",
      answer: "At Life Care and Wellness, our support workers are qualified, well experienced, and passionate about providing high-quality care for people with psychosocial and other disabilities."
    },
    {
      question: "How quickly can I start receiving mental health NDIS support?",
      answer: "We aim to get your supports up and running super quick. If you already have an approved NDIS plan that includes psychosocial or mental health support, you can often start receiving services within a few days to a couple of weeks, depending on availability and your individual needs."
    },
    {
      question: "What is considered a psychosocial disability under the NDIS?",
      answer: "At Life Care and Wellness, we recognise that conditions like anxiety, depression, and other mental health challenges can be considered psychosocial disabilities under the NDIS. These conditions can create real or perceived difficulties in everyday life, affecting how a person thinks, feels, and interacts with the world. Psychosocial disabilities are interlinked to a wide range of mental, emotional, and cognitive experiences, and we are here to provide the support you need to manage them and move forward with confidence."
    },
    {
      question: "Can I change my current mental health NDIS provider?",
      answer: "Yes, you can change your NDIS mental health provider. Participants have the right to choose and change their providers to make sure their support aligns with their needs and goals. If you are not satisfied with your current provider, you can explore other options and make a change."
    },
    {
      question: "What mental health conditions are supported under the NDIS?",
      answer: "The NDIS supports a range of mental health conditions like schizoid disorders, anxiety disorders, stress disorder, mood disorders and depression."
    }
  ];

  const scrollToContact = () => {
    window.location.href = '/contact';
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO title="NDIS Mental Health Support Services" description="Compassionate NDIS mental health support services in Brisbane. We empower individuals with psychosocial disabilities to live independently and confidently." />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <div>
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                NDIS Mental Health Support Services
              </h1>
              <p 
                className="text-lg md:text-xl leading-relaxed mb-4"
                style={{ color: '#5e5e5e' }}
              >
                Living with a mental health condition can feel lonely, exhausting, and misunderstood. Many providers treat mental health as a checklist — we treat it as your whole world. Because it is.
              </p>
              <p 
                className="text-lg leading-relaxed mb-4"
                style={{ color: '#5e5e5e' }}
              >
                In simple terms — we help you build the skills, confidence, and support network you need to live a life that feels good. Not just "managed" — but genuinely good. Our team in Brisbane and Logan is here to walk beside you, not ahead of you.
              </p>
              <p 
                className="text-base leading-relaxed italic mb-4"
                style={{ color: '#6c935e' }}
              >
                We see the psychological struggles that go unseen. You are heard and valued and in control.
              </p>
              <p 
                className="text-sm font-semibold mb-6"
                style={{ color: '#587740' }}
              >
                Free consultation and personalised action plan — let us understand your needs before anything else.
              </p>
              <Button
                onClick={scrollToContact}
                size="lg"
                className="text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#81b956' }}
              >
                <Phone className="w-5 h-5 mr-2" />
                Contact Us
              </Button>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={images.mentalHealth}
                alt="NDIS Mental Health Support Services"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img 
                  src={images.mentalHealth2}
                  alt="Group mental health support session"
                  className="w-full h-[350px] object-cover"
                />
              </div>
              <div>
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-6"
                  style={{ color: '#587740' }}
                >
                  Expert Mental Health Support
                </h2>
                <p 
                  className="text-lg leading-relaxed mb-4"
                  style={{ color: '#5e5e5e' }}
                >
                  The NDIS service provider who offer support services and lifelong funding to people 
                  with mental health concerns helping individuals work towards their recovery goals and 
                  live with hope and optimism. There is no better mental health NDIS provider than Life 
                  Care and Wellness community for mental illness support in Australia.
                </p>
                <p 
                  className="text-lg leading-relaxed mb-4"
                  style={{ color: '#5e5e5e' }}
                >
                  We are working effectively to shine a light on this stigmatised condition which are 
                  overlooked health concerns in Australia and with the NDIS funding we are providing 
                  expert assistance.
                </p>
                <p 
                  className="text-lg leading-relaxed"
                  style={{ color: '#5e5e5e' }}
                >
                  Life Care and Wellness stands among Brisbane's leading mental health NDIS providers 
                  which are highly skilled and trained to provide the expert assistance you need to live 
                  your life to the fullest.
                </p>
              </div>
            </div>

            <div 
              className="p-8 rounded-2xl text-center"
              style={{ backgroundColor: '#f8f9fa' }}
            >
              <p 
                className="text-xl leading-relaxed"
                style={{ color: '#5e5e5e' }}
              >
                Our well experienced NDIS Mental Health Support workers get to know your personal goals 
                and aspirations. We are here to assist you in customising a support plan towards a more 
                fulfilling and independent quality of life.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services Section */}
      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Our Mental Health NDIS Services
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
              <div>
                <p 
                  className="text-lg leading-relaxed mb-6"
                  style={{ color: '#5e5e5e' }}
                >
                  The NDIS can provide support for people with mental health conditions who also have a 
                  psychosocial disability that is likely to be permanent and significantly impacts their 
                  daily life. This means the NDIS can fund supports that help individuals manage their 
                  psychosocial disability and participate more fully in the community, rather than directly 
                  funding clinical treatment for mental health conditions.
                </p>
                <p 
                  className="text-lg leading-relaxed mb-6"
                  style={{ color: '#5e5e5e' }}
                >
                  If you need support for your psychosocial disability or mental health condition, we are 
                  here to walk along with you. Get in touch today or book a free consultation.
                </p>
                <p 
                  className="text-lg leading-relaxed font-semibold"
                  style={{ color: '#587740' }}
                >
                  You may be eligible for this service if you have a mental illness, have severe difficulty 
                  with day to day tasks or need help to apply for access to NDIS for support.
                </p>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src={images.mentalHealth1}
                  alt="Adolescent mental health support"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expectations Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Things You Can Expect in NDIS Mental Health Services
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {expectations.map((expectation, index) => (
                <div 
                  key={index}
                  className="flex items-start space-x-4 p-6 rounded-xl hover:shadow-md transition-shadow"
                  style={{ backgroundColor: '#f8f9fa' }}
                >
                  <CheckCircle2 
                    className="w-6 h-6 flex-shrink-0 mt-1"
                    style={{ color: '#81b956' }}
                  />
                  <p 
                    className="text-base leading-relaxed"
                    style={{ color: '#5e5e5e' }}
                  >
                    {expectation}
                  </p>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Button
                onClick={scrollToContact}
                size="lg"
                className="text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#81b956' }}
              >
                <Phone className="w-5 h-5 mr-2" />
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Benefits of Choosing Our NDIS Mental Health Services
              </h2>
              <p 
                className="text-lg md:text-xl"
                style={{ color: '#5e5e5e' }}
              >
                When looking for an NDIS disability service provider in Brisbane, you will make sure you 
                are choosing a provider who is registered with the National Disability Insurance Scheme.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <Card 
                    key={index}
                    className="border-none shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <CardContent className="p-8 text-center">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto"
                        style={{ backgroundColor: '#AABC5A' }}
                      >
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <p 
                        className="text-base leading-relaxed"
                        style={{ color: '#5e5e5e' }}
                      >
                        {benefit.text}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Why Choose Us as Your Mental Health NDIS Provider?
              </h2>
              <p 
                className="text-lg md:text-xl"
                style={{ color: '#5e5e5e' }}
              >
                Truly speaking, finding the right mental health NDIS provider is not an easy task. 
                The more people talk about mental health, the more we realise that having the right 
                support team can change everything.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src={images.mentalHealth3}
                  alt="Professional mental health consultation"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="space-y-4">
                {whyChooseUs.slice(0, 6).map((reason, index) => (
                  <div 
                    key={index}
                    className="flex items-start space-x-3 p-4 rounded-lg hover:shadow-md transition-shadow"
                    style={{ backgroundColor: '#f8f9fa' }}
                  >
                    <CheckCircle2 
                      className="w-5 h-5 flex-shrink-0 mt-1"
                      style={{ color: '#81b956' }}
                    />
                    <p 
                      className="text-base leading-relaxed"
                      style={{ color: '#5e5e5e' }}
                    >
                      {reason}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {whyChooseUs.slice(6).map((reason, index) => (
                <div 
                  key={index}
                  className="flex items-start space-x-3 p-4 rounded-lg hover:shadow-md transition-shadow"
                  style={{ backgroundColor: '#f8f9fa' }}
                >
                  <CheckCircle2 
                    className="w-5 h-5 flex-shrink-0 mt-1"
                    style={{ color: '#81b956' }}
                  />
                  <p 
                    className="text-base leading-relaxed"
                    style={{ color: '#5e5e5e' }}
                  >
                    {reason}
                  </p>
                </div>
              ))}
            </div>

            <div 
              className="mt-12 p-8 rounded-2xl text-center"
              style={{ backgroundColor: '#f8f9fa' }}
            >
              <p 
                className="text-lg leading-relaxed"
                style={{ color: '#5e5e5e' }}
              >
                As a trusted mental health NDIS provider in Logan the services we provide and help NDIS 
                participants in Brisbane and Logan to live more fulfilling and independent lives by 
                connecting them with the support they need to achieve their goals. These services help 
                participants build skills, connect with others, find employment opportunities, and access 
                community activities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Frequently Asked Questions
              </h2>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="border rounded-lg px-6 shadow-sm bg-white"
                >
                  <AccordionTrigger 
                    className="text-left hover:no-underline py-6"
                    style={{ color: '#587740' }}
                  >
                    <span className="text-lg font-semibold pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent 
                    className="text-base leading-relaxed pb-6"
                    style={{ color: '#5e5e5e' }}
                  >
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="text-center mt-12">
              <p 
                className="text-xl font-semibold mb-6"
                style={{ color: '#587740' }}
              >
                Call Us today and let's make your NDIS journey easier, together.
              </p>
              <Button
                onClick={scrollToContact}
                size="lg"
                className="text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#81b956' }}
              >
                <Phone className="w-5 h-5 mr-2" />
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <VideoBackground variant="dark" className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Your Mental Health Matters — And So Do You
            </h2>
            <p className="text-lg md:text-xl mb-8 text-white/90">
              You deserve support that genuinely understands what you're going through. Free consultation, free action plan — let's have an honest conversation about how we can help you feel like yourself again.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={scrollToContact}
                size="lg"
                className="text-base px-8 py-6"
                style={{ backgroundColor: '#FFFFFF', color: '#587740' }}
              >
                <Phone className="w-5 h-5 mr-2" />
                Get Your Free Consultation
              </Button>
              <a href="/get-on-ndis-free">
                <Button
                  size="lg"
                  variant="outline"
                  className="text-base px-8 py-6 border-2 border-white text-white hover:bg-white/10"
                >
                  Get on the NDIS Free
                </Button>
              </a>
            </div>
          </div>
        </div>
      </VideoBackground>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default MentalHealthPage;
